import { IAccountActions } from "@/account/account-actions";
import { IHeaderModalActions } from "@/common/header-modal/header-modal-actions";
import { createReducer } from "@reduxjs/toolkit";
import { IFeedActions } from "./feed-actions";
import type { IGetFeedConfig } from "./feed-helpers";
import type { IFeedInfo } from "./feed-models";

export interface IFeedModel {
  feeds: null | IFeedInfo[];
  isSubscribed: boolean;
  nextFeedUrl: string | null;
  unreadFeeds: number;
  nextFeedUrlById: string | null;
}

const INITIAL_STATE: IFeedModel = {
  feeds: null,
  isSubscribed: false,
  nextFeedUrl: null,
  unreadFeeds: 0,
  nextFeedUrlById: null,
};

/** Reducer */
export const feedReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(IFeedActions.initGetFeed, (state, action): IFeedModel => {
      const { isForceLoader } = action.payload.config as IGetFeedConfig;
      const nextState = {
        // feeds: isInitialRequest ? null : state.feeds
        feeds: isForceLoader ? null : state.feeds,
      };
      return {
        ...state,
        ...nextState,
      };
    })
    .addCase(IFeedActions.successGetFeed, (state, action): IFeedModel => {
      const config: IGetFeedConfig = action.payload.config;
      const res = action.payload.res;

      let feeds: IFeedInfo[] = res.data;
      const getFeedNextUrl = res.next;
      const isSubscribed: boolean = res.is_subscribed;

      if (!config.isInitialRequest) {
        feeds = [...(state.feeds || []), ...(res.data || [])];
      } else {
        if (state.feeds && state.feeds.length > 0) {
          // e.g. pull down to refresh case
          // we need to persist client side feeds here

          // get current client side feeds if any
          const clientSideFeeds = state.feeds.filter(
            ({ media }) => media?.isClientSide
          );

          // prepend the existing client side feeds to the new feeds
          if (clientSideFeeds.length > 0) {
            feeds = [...clientSideFeeds, ...(res.data || [])];
          }
        }
      }

      return {
        ...state,
        nextFeedUrl: getFeedNextUrl,
        isSubscribed,
        // unreadFeeds: res.unread_feeds,
        unreadFeeds: 0, // reset
        feeds,
      };
    })
    .addCase(IFeedActions.initPostFmFeed, (state, action) => {
      return {
        ...state,
        feeds: [action.payload.clientSideFeedItem, ...(state.feeds || [])],
      };
    })
    .addCase(IFeedActions.progressPostFmFeed, (state, action): IFeedModel => {
      return {
        ...state,
        feeds: (state.feeds || []).map((feedItem) => {
          if (feedItem._id === action.payload.clientSideFeedItem._id) {
            return action.payload.clientSideFeedItem;
          }
          return feedItem;
        }),
      };
    })
    .addCase(IFeedActions.successPostFmFeed, (state, action): IFeedModel => {
      const { res, clientSideId } = action.payload;
      return {
        ...state,
        feeds: (state.feeds || []).map((feedItem) => {
          if (feedItem._id === clientSideId) {
            return res;
          }
          return feedItem;
        }),
      };
    })
    .addCase(IFeedActions.failurePostFmFeed, (state, action): IFeedModel => {
      return {
        ...state,
        feeds: (state.feeds || []).filter(
          (feedItem) => feedItem._id !== action.payload.clientSideFeedItem._id
        ),
      };
    })
    .addCase(IFeedActions.feedItemPatched, (state, action): IFeedModel => {
      const { data } = action.payload;
      return {
        ...state,
        feeds: (state.feeds || []).map((feedItem) =>
          feedItem._id === data._id ? data : feedItem
        ),
      };
    })
    .addCase(
      IHeaderModalActions.feedItemDeleted,
      (state, action): IFeedModel => {
        const { id } = action.payload;
        return {
          ...state,
          feeds: (state.feeds || []).filter(
            (feedItem: IFeedInfo) => feedItem._id !== id
          ),
        };
      }
    )
    .addCase(IFeedActions.setUnreadFeeds, (state, action): IFeedModel => {
      return {
        ...state,
        unreadFeeds: action.payload,
      };
    })
    .addCase(IAccountActions.successGetUser, (state, action): IFeedModel => {
      return {
        ...state,
        unreadFeeds: action.payload.unread_feeds || 0,
      };
    });
});
